/**
 * Business context — the P3 facts-split (unification spec §2).
 *
 * Two honest halves instead of one blended blob:
 *
 * - BRAND facts (name, type, industry, audience, voice, scan data) resolve
 *   KIT-FIRST: effective brand kit → that kit's default voice → only then the
 *   legacy newest-voice-row fallback. The old implementation read the newest
 *   `user_brand_voices` row for the whole account, which is exactly the vector
 *   that leaked one brand's stale scan data into another brand's Board on
 *   multi-kit accounts (2026-07-01 audit).
 * - COMMERCE/ACCOUNT facts (products, competitors, size, goals) are account-
 *   scoped by nature and stay a separate lookup — they belong to the store,
 *   not to any one brand kit.
 *
 * `getBusinessContext()` composes both for existing consumers
 * (useBusinessContext → ContentBoardPage). Note the old code also read
 * `voice_description` and `tone_attributes` — columns that do not exist on
 * `user_brand_voices` — so brandVoice/toneAttributes silently fell to their
 * hardcoded defaults for every user; they now read the real columns.
 */

import { supabase } from '@/integrations/supabase/client';
import { workspaceOrFilter } from '@/lib/ai-agent/tools/workspace-scope';
import { preferCanonicalText } from '@/lib/brand/merge-canonical-legacy';
import { resolveActiveBrandKitId } from '@/lib/identity/resolveActiveBrandKitId';
import { resolveOwnerId } from '@/lib/identity/resolveOwnerId';
import { brandKitService } from '@/services/data/BrandKitService';
import { brandVoiceService } from '@/services/data/BrandVoiceService';
import { logger } from '@/utils/logger';

export interface BrandFacts {
  businessName: string;
  businessType: string;
  industry: string;
  targetAudience: string;
  brandVoice: string;
  communicationStyle: string;
  toneAttributes: string[];
  website_scan_data?: Record<string, unknown> | null;
  websiteUrl?: string | null;
}

export interface CommerceFacts {
  businessSize: 'startup' | 'small' | 'medium' | 'enterprise';
  primaryGoals: string[];
  painPoints: string[];
  hasProducts: boolean;
  hasCompetitors: boolean;
  competitors?: string[];
}

export interface BusinessContext extends BrandFacts, CommerceFacts {}

type VoiceRow = Record<string, unknown> | null;

/**
 * Kit default voice, else the legacy newest UNATTRIBUTED voice row (pre-kit
 * users, `brand_kit_id IS NULL`).
 *
 * The `.is('brand_kit_id', null)` is load-bearing, not decoration. Without it
 * this fallback is a plain "newest row for this account" query with no kit
 * filter at all — on a multi-kit account it can hand back a DIFFERENT kit's
 * voice row (or an orphaned row meant for yet another kit) whenever the
 * requested kit has zero `user_brand_voices` rows of its own, which
 * `getKitDefaultVoice` cannot distinguish from "the kit has a row but it
 * isn't marked default." Verified live 2026-08-27 on the ITC Tax workspace
 * (zero voice rows of its own): this fallback returned a DIFFERENT kit's
 * default voice row when a same-account orphaned row didn't happen to be
 * newer, and its content pillars rendered on ITC Tax's board under ITC
 * Tax's own name — exactly the cross-brand leak this file's module
 * docstring says the kit-first split was written to prevent.
 */
async function resolveVoiceRow(kitId: string | null, ownerId: string): Promise<VoiceRow> {
  if (kitId) {
    const res = await brandVoiceService.getKitDefaultVoice(kitId);
    if (res.data) return res.data as unknown as Record<string, unknown>;
  }
  const { data } = await supabase
    .from('user_brand_voices')
    .select('*')
    .eq('user_id', ownerId)
    .is('brand_kit_id', null)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  return data ?? null;
}

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);

/**
 * Company type is an account fact, shared by every workspace. It is the LAST
 * fallback: an explicit per-brand value still wins because each workspace can
 * have a different brand identity from the agency/company that owns it.
 */
async function resolveAccountCompanyType(ownerUserId: string): Promise<string | null> {
  const { data } = await supabase
    .from('accounts')
    .select('company_type')
    .eq('owner_user_id', ownerUserId)
    .maybeSingle();
  return str(data?.company_type);
}

/** Brand facts, kit-first (field map: spec §1 rule 5; D-A business_type in guidelines). */
export async function getBrandFacts(): Promise<BrandFacts | null> {
  try {
    const ownerId = await resolveOwnerId('');
    const kit = await brandKitService.getBrandKit().catch(() => null);
    const voice = await resolveVoiceRow(kit?.id ?? null, ownerId);
    const accountCompanyType = await resolveAccountCompanyType(ownerId).catch(() => null);

    const guidelines = (kit?.brand_guidelines ?? {}) as Record<string, unknown>;
    const guidelinesBrand = (guidelines.brand ?? {}) as Record<string, unknown>;
    const guidelinesAudience = (guidelines.audience ?? {}) as Record<string, unknown>;
    const guidelinesVoice = (guidelines.voice ?? {}) as Record<string, unknown>;
    const attrs = (voice?.voice_attributes ?? {}) as Record<string, unknown>;
    const toneDescriptors = Array.isArray(attrs.toneDescriptors)
      ? (attrs.toneDescriptors as string[]).filter((t) => typeof t === 'string')
      : [];

    /*
     * 🔴 CANONICAL-FIRST, VIA THE SHARED RULE — THIS FILE USED TO BE SPLIT.
     *
     * `businessType` was already guidelines-first (decision D-A), but
     * `industry`, `targetAudience`, `brandVoice` and `communicationStyle` read
     * `user_brand_voices` and NOTHING ELSE — `brand_guidelines` never entered
     * the expression. One file, two precedence rules, no comment saying so.
     *
     * That is not academic. `getBrandFacts()` is what `useBusinessContext()`
     * returns, and it feeds Discover's ICP widget and context panel, the
     * Create-Plan dialog's defaults, the Content Board and the Home composer —
     * none of which label where a value came from. Measured on production
     * 2026-09-09: Kestrel Analytics has `brand_guidelines.brand.industry =
     * 'software'` and no `user_brand_voices` row at all, so this function
     * returned the hardcoded 'business' while the Brand profile page, which
     * reads canonical, displayed 'software' on the same workspace.
     *
     * These now go through `preferCanonicalText` — the same rule BrandSettings
     * and the system prompt builder use — so there is one answer to "which
     * store wins", written once, rather than a fresh guess per call site.
     */
    return {
      businessName: str(kit?.name) || str(voice?.profile_name) || 'Your Business',
      businessType:
        str(guidelinesBrand.business_type) ||
        str(voice?.business_type) ||
        accountCompanyType ||
        'general',
      industry:
        preferCanonicalText(str(guidelinesBrand.industry), str(voice?.industry)) || 'business',
      targetAudience:
        preferCanonicalText(
          str(guidelinesAudience.icp) || str(guidelinesAudience.description),
          str(voice?.target_audience) || str(voice?.audience_description),
        ) || 'general audience',
      brandVoice:
        preferCanonicalText(str(guidelinesVoice.personality), str(voice?.brand_personality)) ||
        'professional and engaging',
      communicationStyle:
        preferCanonicalText(
          str(guidelinesVoice.archetype) || str(guidelinesVoice.tone),
          str(voice?.communication_style) || str(voice?.tone),
        ) || 'professional',
      toneAttributes: toneDescriptors.length ? toneDescriptors : ['professional', 'friendly'],
      website_scan_data: (voice?.website_scan_data as Record<string, unknown> | null) ?? null,
      // The kit column is canonical for the website now — the duplicate editor
      // that wrote `user_brand_voices.website_url` was removed 2026-09-09.
      websiteUrl: preferCanonicalText(str(kit?.website_url), str(voice?.website_url)),
    };
  } catch (error) {
    logger.error('Error fetching brand facts:', error);
    return null;
  }
}

/** Account/commerce facts — store-level, deliberately NOT kit-scoped. */
export async function getCommerceFacts(): Promise<CommerceFacts | null> {
  try {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return null;

    const ownerId = await resolveOwnerId(user.id);

    const { count: productsCount } = await supabase
      .from('shopify_products')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', ownerId);

    /*
     * 🔴 SCOPED TO THE ACTIVE WORKSPACE. THIS READ FED ANOTHER CLIENT'S
     * COMPETITORS INTO GENERATION.
     *
     * This was `.eq('user_id', ownerId)` and nothing else. `competitors` is
     * workspace-scoped by `brand_kit_id`, so on an account with more than one
     * workspace it returned EVERY workspace's competitors — and this function
     * is `getCommerceFacts`, whose output is the brand context behind the Home
     * composer and article generation.
     *
     * Measured on the founder's own account: ITC Tax, a B2B property-tax
     * consultancy, has zero competitors of its own, and this returned Buffer,
     * Hootsuite, Later, Babolat Tennis and Wilson Sporting Goods — all created
     * before ITC Tax existed and all belonging to Mora AI Studio. Articles for
     * a tax consultancy were being grounded against social schedulers and
     * tennis brands, and the founder saw those names on surfaces where ITC was
     * the active workspace.
     *
     * `brand_kit_id.is.null` is kept in the filter deliberately: rows predating
     * workspaces are unattributed, and dropping them would empty the list for
     * single-workspace accounts, which is the opposite failure.
     */
    const activeKitId = await resolveActiveBrandKitId().catch(() => null);
    /*
     * 🔴 ONLY ACTUAL COMPETITORS. `competitors` holds three relationship kinds
     * since the 2026-09-09 consolidation — `competitor`, `inspiration` and
     * `client_account` — and this query had no filter, so it handed the model
     * an agency's own CLIENT ROSTER and a brand's INSPIRATION accounts labelled
     * as competitors. `src/lib/plans/run-grounding.ts:265` already filters;
     * this reader, which feeds the Home composer and article generation, did
     * not. Same table, two readers, one of them wrong.
     *
     * ⚠️ `.or(…is.null)` is DEFENSIVE, not a description of today's data — all
     * 33 production rows are `competitor` right now (checked 2026-09-09), so
     * this changes nothing yet. It is here because the kind was added to carve
     * NEW meanings out of an existing table: any row written by a path that has
     * not been taught the column, or created before a future backfill, is a
     * competitor and must not silently vanish from the model's context. An
     * empty competitor list reads as "this brand has no competitors", which is
     * a claim, not an absence.
     */
    let competitorsQuery = supabase
      .from('competitors')
      .select('name', { count: 'exact' })
      .eq('user_id', ownerId)
      // Strict: `relationship_kind` is NOT NULL DEFAULT 'competitor', so the
      // NULL-tolerant form this used to carry could never match anything extra.
      .eq('relationship_kind', 'competitor');
    const scope = workspaceOrFilter(activeKitId);
    if (scope) competitorsQuery = competitorsQuery.or(scope);
    const { count: competitorsCount, data: competitorRows } = await competitorsQuery.limit(10);

    const competitors = (competitorRows ?? [])
      .map((r) => (r as { name: string | null }).name)
      .filter((n): n is string => !!n);

    return {
      // No storage backs these three: user_profiles has no business_size /
      // primary_goals / pain_points columns. The old select('*') read them as
      // undefined and silently served these same defaults while LOOKING like
      // data. Stated defaults until a real column exists — don't re-add the
      // phantom read.
      businessSize: 'small',
      primaryGoals: ['growth', 'engagement'],
      painPoints: ['time management', 'content creation'],
      hasProducts: (productsCount || 0) > 0,
      hasCompetitors: (competitorsCount || 0) > 0,
      competitors,
    };
  } catch (error) {
    logger.error('Error fetching commerce facts:', error);
    return null;
  }
}

/** Composed view for existing consumers (useBusinessContext → Board). */
export const getBusinessContext = async (): Promise<BusinessContext | null> => {
  const [brand, commerce] = await Promise.all([getBrandFacts(), getCommerceFacts()]);
  if (!brand && !commerce) return null;
  return {
    ...(brand ?? {
      businessName: 'Your Business',
      businessType: 'general',
      industry: 'business',
      targetAudience: 'general audience',
      brandVoice: 'professional and engaging',
      communicationStyle: 'professional',
      toneAttributes: ['professional', 'friendly'],
      website_scan_data: null,
      websiteUrl: null,
    }),
    ...(commerce ?? {
      businessSize: 'small' as const,
      primaryGoals: ['growth', 'engagement'],
      painPoints: ['time management', 'content creation'],
      hasProducts: false,
      hasCompetitors: false,
      competitors: [],
    }),
  };
};
